/**
 * The site's header and footer, as data.
 *
 * Written once and rendered twice: by the static pages through the `html` template
 * (`site/document.ts`) and by the interactive pages in React (`ui/SiteBar.tsx`, the
 * configurator's own header). A link added to one and not the other is the drift this
 * file exists to stop, so neither keeps a list of its own.
 *
 * The header lists each plan type's catalog and configurator, then what is the
 * site's rather than a type's. The footer carries the terms, every plan type's
 * reference and the plans page, under one sentence true of every plan sold.
 */

import { HOME_PATH, PLANS_PATH, TERMS_PATH } from "./paths.js";
import { PLAN_TYPES, planType, type PlanType } from "./planTypes.js";

export interface NavLink {
  label: string;
  href: string;
}

export const BRAND = "Parametric Plans";

export const BRAND_LINK: NavLink = { label: BRAND, href: HOME_PATH };

export const HEADER_LINKS: readonly NavLink[] = [
  ...PLAN_TYPES.flatMap((type) => [
    { label: `${type.name}`, href: type.catalogPath },
    { label: `Design a ${type.singular}`, href: type.designerPath },
  ]),
  { label: "Your plans", href: PLANS_PATH },
];

export const FOOTER_LINKS: readonly NavLink[] = [
  { label: "Terms of sale", href: TERMS_PATH },
  ...PLAN_TYPES.map((type) => ({
    label: PLAN_TYPES.length === 1 ? "Reference & glossary" : `${type.name}: reference & glossary`,
    href: type.referencePath,
  })),
  { label: "Your plans", href: PLANS_PATH },
];

export const FOOTER_NOTE =
  `A plan from ${BRAND} is a set of drawings for building it yourself. It is not an ` +
  "engineered design, and permits are yours to check before you build.";

/**
 * What a plan type's configurator links to from its panel's header: its own catalog and
 * reference, then the site's pages. The panel has no room for the bar, so it carries
 * these instead, and the brand above them goes home.
 */
export function designerLinks(id: PlanType): readonly NavLink[] {
  const type = planType(id);
  return [
    { label: "Ready-made designs", href: type.catalogPath },
    { label: "Reference & glossary", href: type.referencePath },
    { label: "Your plans", href: PLANS_PATH },
    { label: "Terms", href: TERMS_PATH },
  ];
}
